<template>
  <div class="config-sku-stock">
    <!-- 顶部导航栏 -->
    <div class="header">
      <div class="back-button" @click="goBack">
        <el-icon class="back-icon"><ArrowLeft /></el-icon>
      </div>
      <h1>SKU库存配置</h1>
    </div>

    <div class="content">
      <!-- 选择区域 -->
      <div class="card">
        <div class="form-row">
          <div class="form-label">SPU套餐</div>
          <el-select
            v-model="selectedSpu"
            placeholder="请选择SPU套餐"
            @change="handleSpuChange"
            class="full-select"
          >
            <el-option
              v-for="spu in spuList"
              :key="spu.id"
              :label="spu.name"
              :value="spu.id"
            />
          </el-select>
        </div>
        <div class="form-row">
          <div class="form-label">设备规格</div>
          <el-select
            v-model="selectedNorm"
            placeholder="请选择规格"
            @change="handleNormChange"
            class="full-select"
            :disabled="!selectedSpu"
          >
            <el-option
              v-for="norm in normList"
              :key="norm.id"
              :label="norm.name"
              :value="norm.id"
            />
          </el-select>
        </div>
      </div>

      <!-- 库存概览（选择规格后显示） -->
      <div class="card" v-if="selectedNorm">
        <div class="overview-title">库存概览</div>
        <div class="progress-wrapper">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{
                width: Math.min(100, stockPercent) + '%',
                backgroundColor: progressColor,
              }"
            ></div>
          </div>
          <div class="progress-text" :style="{ color: progressColor }">
            {{ stockPercent }}%
          </div>
        </div>
        <div class="overview-stats">
          <div class="stat-item">
            <div class="stat-value">{{ categoryStock }}</div>
            <div class="stat-label">总库存</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value">{{ allocatedStock }}</div>
            <div class="stat-label">已配置</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value" :class="{ danger: isOverLimit }">
              {{ remainingStock }}
            </div>
            <div class="stat-label">剩余可配置</div>
          </div>
        </div>
        <div class="over-limit-tip" v-if="isOverLimit">
          <el-icon><WarningFilled /></el-icon>
          库存配置超出总库存 {{ Math.abs(remainingStock) }}，请调整
        </div>
      </div>

      <!-- 批量操作（选择规格后显示） -->
      <div class="card action-bar" v-if="selectedNorm && skuList.length">
        <div class="action-btns">
          <el-button size="small" @click="distributeEvenly" :disabled="remainingStock <= 0">
            <el-icon><Share /></el-icon>
            平均分配
          </el-button>
          <el-button size="small" @click="clearAllStock" :disabled="allocatedStock === 0">
            <el-icon><Delete /></el-icon>
            全部清零
          </el-button>
        </div>
        <div class="dirty-count" v-if="dirtyCount > 0">
          已修改 <span class="count-num">{{ dirtyCount }}</span> 项
        </div>
      </div>

      <!-- SKU列表 -->
      <div class="card sku-section" v-if="selectedNorm">
        <div class="section-title" v-if="skuList.length">SKU列表</div>
        <div class="empty-tip" v-if="!skuList.length">暂无SKU数据</div>

        <div class="sku-list">
          <div
            v-for="sku in skuList"
            :key="sku.id"
            class="sku-card"
            :class="{ 'sku-dirty': isDirty(sku), 'sku-over': isOverLimit }"
          >
            <div class="sku-top">
              <div class="sku-main">
                <div class="sku-name">{{ sku.goodsName }}</div>
                <div class="sku-meta">
                  <span class="meta-tag">
                    <el-icon><Timer /></el-icon>
                    {{ formatSeconds(sku.seconds) }}
                  </span>
                  <span class="meta-tag price">
                    ¥{{ sku.lowPrice }}
                  </span>
                </div>
              </div>
              <div class="sku-stock-input">
                <el-input-number
                  v-model="sku.stock"
                  :min="0"
                  :max="getSkuMax(sku)"
                  size="small"
                  controls-position="right"
                />
              </div>
            </div>
            <div class="sku-bar-wrapper">
              <div class="sku-bar">
                <div
                  class="sku-bar-fill"
                  :style="{
                    width: getSkuPercent(sku) + '%',
                    backgroundColor: getSkuBarColor(sku),
                  }"
                ></div>
              </div>
              <div class="sku-bar-text">库存 {{ sku.stock }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部固定操作栏 -->
    <div class="footer-bar" v-if="selectedNorm && skuList.length">
      <div class="footer-info">
        <span class="footer-label">剩余可配置</span>
        <span class="footer-value" :class="{ danger: isOverLimit }">
          {{ remainingStock }}
        </span>
      </div>
      <el-button
        type="primary"
        class="save-btn"
        :loading="saveLoading"
        :disabled="isOverLimit || dirtyCount === 0"
        @click="handleSave"
      >
        {{ saveLoading ? '保存中...' : `保存${dirtyCount > 0 ? '(' + dirtyCount + '项)' : ''}` }}
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import request from "@/utils/request.ts";
import { ElMessage, ElIcon } from "element-plus";
import {
  ArrowLeft,
  WarningFilled,
  Share,
  Delete,
  Timer,
} from "@element-plus/icons-vue";
import API_CONFIG from "./api.json";
import { useRouter } from "vue-router";

const router = useRouter();

// ==================== 数据状态 ====================
const spuList = ref([]);
const normList = ref([]);
const skuList = ref([]);
const selectedSpu = ref(null);
const selectedNorm = ref(null);
const categoryStock = ref(0);
const originalStockMap = ref({});
const saveLoading = ref(false);

// ==================== 计算属性 ====================
// 已配置总库存
const allocatedStock = computed(() =>
  skuList.value.reduce((sum, sku) => sum + (sku.stock || 0), 0)
);

// 剩余可配置库存
const remainingStock = computed(() =>
  categoryStock.value - allocatedStock.value
);

// 库存分配百分比
const stockPercent = computed(() =>
  categoryStock.value > 0
    ? Math.min(100, Math.round((allocatedStock.value / categoryStock.value) * 100))
    : 0
);

// 进度条颜色
const progressColor = computed(() => {
  if (stockPercent.value > 95) return "#f56c6c";
  if (stockPercent.value > 80) return "#e6a23c";
  return "#409eff";
});

// 是否超限
const isOverLimit = computed(() => remainingStock.value < 0);

// 修改过的 SKU 数量
const dirtyCount = computed(() =>
  skuList.value.filter((sku) => sku.stock !== originalStockMap.value[sku.id]).length
);

// ==================== 方法 ====================

// 格式化秒数为时分秒
const formatSeconds = (seconds) => {
  if (!seconds) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

// 获取单个 SKU 的动态 max
const getSkuMax = (sku) => {
  return sku.stock + remainingStock.value;
};

// SKU 库存占总库存百分比
const getSkuPercent = (sku) => {
  if (categoryStock.value <= 0) return 0;
  return Math.min(100, Math.round(((sku.stock || 0) / categoryStock.value) * 100));
};

// SKU 库存条颜色
const getSkuBarColor = (sku) => {
  const pct = getSkuPercent(sku);
  if (pct > 50) return "#f56c6c";
  if (pct > 30) return "#e6a23c";
  return "#409eff";
};

// 判断 SKU 是否被修改
const isDirty = (sku) => {
  return sku.stock !== originalStockMap.value[sku.id];
};

// 获取规格剩余总库存
const getCategoryStock = (categoryId) => {
  request
    .post("/goods/goodsCategory/pageList", {
      pageDomain: { pageNum: 1, pageSize: 10 },
      id: categoryId,
    })
    .then((res) => {
      if (res.code == 200) {
        categoryStock.value = res.data.data[0]?.stock || 0;
      } else {
        ElMessage.error(res.msg);
      }
    });
};

// 获取SPU列表
const getSpuList = () => {
  request.post(API_CONFIG.querySpu.url).then((res) => {
    if (res.code == 200) {
      spuList.value = res.data || [];
    } else {
      ElMessage.error(res.msg);
    }
  });
};

// 获取SKU列表
const getSkuList = (categoryId, spuId) => {
  request
    .post(API_CONFIG.queryCategoryParams.url, { categoryId, spuId })
    .then((res) => {
      if (res.code == 200) {
        skuList.value = res.data.goodsSkuList || [];
        // 记录原始库存，用于脏检测
        originalStockMap.value = {};
        skuList.value.forEach((sku) => {
          originalStockMap.value[sku.id] = sku.stock;
        });
      } else {
        ElMessage.error(res.msg);
      }
    });
};

// 处理SPU选择变化
const handleSpuChange = (spu) => {
  const currentSpu = spuList.value.find((item) => item.id == spu);
  if (currentSpu) {
    normList.value = currentSpu.goodsCategoryList || [];
    selectedNorm.value = null;
    skuList.value = [];
    categoryStock.value = 0;
    originalStockMap.value = {};
  }
};

// 处理规格选择变化
const handleNormChange = (norm) => {
  if (norm && selectedSpu.value) {
    getSkuList(norm, selectedSpu.value.id);
    getCategoryStock(norm);
  }
};

// 平均分配剩余库存
const distributeEvenly = () => {
  const remaining = remainingStock.value;
  if (remaining <= 0) {
    ElMessage.warning("没有剩余可分配库存");
    return;
  }
  const count = skuList.value.length;
  if (!count) return;
  const avg = Math.floor(remaining / count);
  const remainder = remaining % count;
  skuList.value.forEach((sku, i) => {
    sku.stock += avg + (i < remainder ? 1 : 0);
  });
  ElMessage.success(`已平均分配 ${remaining} 库存`);
};

// 全部清零
const clearAllStock = () => {
  skuList.value.forEach((sku) => {
    sku.stock = 0;
  });
  ElMessage.success("已全部清零");
};

// 批量保存
const handleSave = async () => {
  if (isOverLimit.value) {
    ElMessage.error("库存超限，请调整");
    return;
  }
  const dirty = skuList.value.filter(
    (sku) => sku.stock !== originalStockMap.value[sku.id]
  );
  if (dirty.length === 0) {
    ElMessage.info("没有修改需要保存");
    return;
  }
  saveLoading.value = true;
  try {
    for (const sku of dirty) {
      const res = await request.post(API_CONFIG.updateSku.url, {
        id: sku.id,
        stock: sku.stock,
      });
      if (res.code != 200) {
        ElMessage.error(res.msg || "保存失败");
        break;
      }
    }
    ElMessage.success("保存成功");
    // 更新原始库存记录
    dirty.forEach((sku) => {
      originalStockMap.value[sku.id] = sku.stock;
    });
  } catch {
    ElMessage.error("保存失败，请重试");
  } finally {
    saveLoading.value = false;
  }
};

// 初始化
onMounted(() => {
  getSpuList();
});

// 返回上一页
const goBack = () => {
  router.push("/h5/workPlace");
};
</script>

<style lang="scss" scoped>
.config-sku-stock {
  min-height: 100vh;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  padding-bottom: 70px; // 为底部操作栏留空间

  // 顶部导航
  .header {
    background: linear-gradient(135deg, #409eff, #337ecc);
    color: white;
    padding: 16px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
    position: sticky;
    top: 0;
    z-index: 100;

    h1 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .back-button {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      padding: 4px;

      .back-icon {
        font-size: 20px;
        color: white;
      }
    }
  }

  .content {
    flex: 1;
    padding: 12px;
  }

  // 通用卡片
  .card {
    background-color: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  // 选择区域
  .form-row {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .form-label {
      font-size: 13px;
      color: #909399;
      margin-bottom: 6px;
    }

    .full-select {
      width: 100%;
    }
  }

  // 库存概览
  .overview-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
  }

  .progress-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;

    .progress-bar {
      flex: 1;
      height: 8px;
      background-color: #ebeef5;
      border-radius: 4px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        border-radius: 4px;
        transition: width 0.3s ease, background-color 0.3s ease;
      }
    }

    .progress-text {
      font-size: 14px;
      font-weight: 600;
      min-width: 40px;
      text-align: right;
    }
  }

  .overview-stats {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 10px 0;

    .stat-item {
      text-align: center;
      flex: 1;

      .stat-value {
        font-size: 22px;
        font-weight: 700;
        color: #303133;
        transition: color 0.3s;

        &.danger {
          color: #f56c6c;
        }
      }

      .stat-label {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
      }
    }

    .stat-divider {
      width: 1px;
      height: 30px;
      background-color: #ebeef5;
    }
  }

  .over-limit-tip {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 8px 12px;
    background-color: #fef0f0;
    border-radius: 8px;
    font-size: 13px;
    color: #f56c6c;
  }

  // 批量操作
  .action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .action-btns {
      display: flex;
      gap: 8px;

      .el-button {
        font-size: 13px;
      }
    }

    .dirty-count {
      font-size: 13px;
      color: #909399;

      .count-num {
        color: #409eff;
        font-weight: 600;
      }
    }
  }

  // SKU 列表
  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
  }

  .empty-tip {
    text-align: center;
    color: #909399;
    font-size: 14px;
    padding: 20px 0;
  }

  .sku-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .sku-card {
    background-color: #fafbfc;
    border-radius: 10px;
    padding: 12px;
    border: 1.5px solid transparent;
    transition: border-color 0.2s, box-shadow 0.2s;

    &.sku-dirty {
      border-color: #b3d8ff;
      box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.1);
    }

    &.sku-over {
      border-color: #fab6b6;
      box-shadow: 0 0 0 1px rgba(245, 108, 108, 0.1);
    }

    .sku-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .sku-main {
      flex: 1;
      min-width: 0;

      .sku-name {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 6px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .sku-meta {
        display: flex;
        gap: 8px;

        .meta-tag {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 12px;
          color: #909399;
          background: #f0f2f5;
          padding: 2px 8px;
          border-radius: 4px;

          .el-icon {
            font-size: 12px;
          }

          &.price {
            color: #f56c6c;
            background: #fef0f0;
            font-weight: 500;
          }
        }
      }
    }

    .sku-stock-input {
      min-width: 120px;
      flex-shrink: 0;

      :deep(.el-input-number) {
        width: 120px;

        .el-input__wrapper {
          padding-left: 8px;
          padding-right: 24px;
        }
      }
    }

    .sku-bar-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 10px;

      .sku-bar {
        flex: 1;
        height: 4px;
        background-color: #ebeef5;
        border-radius: 2px;
        overflow: hidden;

        .sku-bar-fill {
          height: 100%;
          border-radius: 2px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }
      }

      .sku-bar-text {
        font-size: 11px;
        color: #909399;
        white-space: nowrap;
      }
    }
  }

  // 底部固定操作栏
  .footer-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
    z-index: 100;

    .footer-info {
      display: flex;
      align-items: baseline;
      gap: 6px;

      .footer-label {
        font-size: 13px;
        color: #909399;
      }

      .footer-value {
        font-size: 20px;
        font-weight: 700;
        color: #303133;
        transition: color 0.3s;

        &.danger {
          color: #f56c6c;
        }
      }
    }

    .save-btn {
      min-width: 120px;
      height: 40px;
      font-size: 15px;
      border-radius: 8px;
    }
  }
}
</style>
